import React, { Fragment, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import InstructionalText from '../notifications/InstructionalText';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';
import { ConditionalRendering } from '@components/common/conditional-rendering';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';
import { useExchangeAndProgramNames, selectMedicaidEndDateOverride } from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { preventerTools } from '@utils/tools/preventerTools';
import { questionsVisibility } from '@utils/miscellaneous/pageAndQuestionsVisibility';
import { subtractFromDateWithFormat } from '@utils/dates/date.utils';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { STATE_NAMES } from '@constants/configs/states';
import { YYYY } from '@utils/dates/dateFormats.utils';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Pregnant Details ' Page
 *
 */
const Page2_21 = props => {

    const {
        data,
        data: {
            taxHousehold: {
                thCommonParams: {
                    hasSomeoneWithMedicaidChipDenial,
                    hasSomeoneWithBHPDenial
                }
            }
        },
        environment: {
            localDates: {
                serverDate
            },
            isState,
            stateCode
        },
        householdMembers,
        form
    } = props;

    const {
        formName,
        medicaidTerminationFeatureEnabled
    } = props;

    const { exchangeName, exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip } = useExchangeAndProgramNames();
    const medicaidEndDateOverride = useSelector(selectMedicaidEndDateOverride);

    const intlApi = useIntl();

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const onFieldChange_member = (...args) => {

        let { indexInArray, name, newData, value } = parseFieldChangeArgs(args);

        const newState = { ...data };
        let isBhp = name.startsWith('bhp');

        newData = {
            ...newData,
            fiveYearBar: false
        };

        switch(true) {
            case name === 'medicaidChipDenial' && !value:
                newData = {
                    ...newData,
                    ...cloneObject(EMPTY_OBJECTS.MEDICAID_CHIP_DENIAL),
                    medicaidChipDenial: value
                };
                break;
            case name === 'bhpDenied':
                newData = {
                    ...newData,
                    ...cloneObject(EMPTY_OBJECTS.BHP_DENIAL),
                    bhpDenied: value
                };
                break;
            case name === 'medicaidDeniedDueToImmigration':
                newData.changeInImmigrationSince5Year = null;
                newData.changeInImmigrationSinceMedicaidDenial = null;
                break;
            case name === 'changeInImmigrationSince5Year':
                newData.changeInImmigrationSinceMedicaidDenial = null;
                break;
        }

        const valuesForWatch = [
            newData.medicaidDeniedDueToImmigration,
            newData.changeInImmigrationSince5Year,
            newData.changeInImmigrationSinceMedicaidDenial
        ];

        newData.fiveYearBar = newData.medicaidChipDenial && valuesForWatch.reduce((res, v, idx) => {
            if(v === false && idx === 0) {
                return false;
            }
            if(v === false) {
                return true;
            }
            return res;
        }, false);

        newState.taxHousehold.householdMember[indexInArray] = { ...newData };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName: isBhp ? 'bhp' : 'chip' }
        );

        props.actions.changeData(syncedData);
    };

    const onFieldChange_none_of_above = (...args) => {

        const { name, newData } = parseFieldChangeArgs(args);
        const isBhp = name === 'hasSomeoneWithBHPDenial';

        const updateIndividualValues = hhm => {
            return {
                ...hhm,
                ...cloneObject(isBhp ? EMPTY_OBJECTS.BHP_DENIAL : EMPTY_OBJECTS.MEDICAID_CHIP_DENIAL),
                [isBhp ? 'bhpDenied' : 'medicaidChipDenial']: false
            };
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName: isBhp ? 'bhp' : 'chip'
            }
        );

        props.actions.changeData(syncedData);
    };

    const getHouseholdMembers = (householdMembers) => {
        const membersVisible = householdMembers
            .map((member, i) => {
                const isVisible = questionsVisibility.isMedicaidChipDenialShown({
                    hhm: member,
                    medicaidTerminationFeatureEnabled,
                    serverDate,
                    medicaidEndDateOverride
                });
                return { member, originalIndex: i, isVisible };
            })
            .filter(({ isVisible }) => isVisible);

        const combinedMembers = membersVisible.map(({ member, originalIndex: i }) => {
            const {
                applicantGuid,
                name,
                medicaidChipDenial,
                medicaidDeniedDate,
                medicaidDeniedDueToImmigration,
                changeInImmigrationSince5Year,
                changeInImmigrationSinceMedicaidDenial,
                citizenshipImmigrationStatus: {
                    citizenshipStatusIndicator
                }
            } = member;

            const memberName = normalizeNameOfPerson(name);
            const fiveYearsAgo = subtractFromDateWithFormat(5, 'year', YYYY, serverDate);

            return (
                <li key = { applicantGuid }>
                    <UniversalInput
                        label = { {
                            text: 'common.input.label.non_internationalized_text',
                            labelTextReplacements: {
                                text: memberName
                            }
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'medicaidChipDenial',
                            classes: [''],
                            checked: medicaidChipDenial,
                            value: medicaidChipDenial,
                            currentData: member,
                            fields: [],
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange_member
                        } }
                        indexInArray={ i }
                    />

                    <ConditionalRendering isTrue={medicaidChipDenial}>
                        <div className = 'grid-container' >
                            <UniversalInput
                                id = 'medicaid_chip_denial_information'
                                label = { {
                                    labelClass: [ 'padding-0' ],
                                    text: 'ssap.input.label.when_denied_medicaid',
                                    labelTextReplacements: {
                                        personName: memberName, exchangeMedicaidOrChip
                                    },
                                    dataInstructions: 'instructional_text.medicaid_chip_denial_information',
                                    replacements: { exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip }
                                } }
                                inputData = { {
                                    inputClass: [ '' ],
                                    isInputRequired: true,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    name: 'medicaidDeniedDate',
                                    value: medicaidDeniedDate,
                                    valuesToMatch: serverDate,
                                    validationType: VALIDATION_TYPES.DATE_VALID_CHIP_DENIAL_DATE,
                                    currentData: member,
                                    fields: []
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange_member
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                                indexInArray = { i }
                            />

                            <ConditionalRendering isTrue={!citizenshipStatusIndicator}>
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.not_eligible_for_medicaid',
                                        legendTextReplacements: {
                                            personName: <strong>{ normalizeNameOfPerson(name) }</strong>,
                                            fiveYearsAgo: <strong>{ fiveYearsAgo }</strong>,
                                            exchangeMedicaid,
                                            exchangeMedicaidOrChip
                                        }
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'medicaidDeniedDueToImmigration',
                                        value: medicaidDeniedDueToImmigration,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                        currentData: member,
                                        fields: []
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_member
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                    indexInArray = { i }
                                />
                            </ConditionalRendering>

                            <ConditionalRendering isTrue={medicaidDeniedDueToImmigration}>
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.change_in_immigration_status',
                                        legendTextReplacements: {
                                            personName: <strong>{ normalizeNameOfPerson(name) }</strong>,
                                            fiveYearsAgo: <strong>{ fiveYearsAgo }</strong>
                                        }
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'changeInImmigrationSince5Year',
                                        value: changeInImmigrationSince5Year,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                        currentData: member,
                                        fields: []
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_member
                                    } }
                                    indexInArray = { i }
                                />
                            </ConditionalRendering>

                            <ConditionalRendering isTrue={changeInImmigrationSince5Year}>
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.change_after_check_up',
                                        legendTextReplacements: {
                                            personName: <strong>{ normalizeNameOfPerson(name) }</strong>,
                                            stateName: STATE_NAMES[stateCode],
                                            exchangeName,
                                            exchangeMedicaid,
                                            exchangeMedicaidOrChip
                                        }
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'changeInImmigrationSinceMedicaidDenial',
                                        value: changeInImmigrationSinceMedicaidDenial,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                        currentData: member,
                                        fields: []
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_member
                                    } }
                                    indexInArray = { i }
                                />
                            </ConditionalRendering>
                        </div>
                    </ConditionalRendering>
                </li>
            );
        });

        return (
            <ul className="usa-list usa-list--unstyled" role="group" aria-labelledby="medicaid_chip_denial_information_legend_text">
                {combinedMembers}

                <li key = 'hasSomeoneWithMedicaidChipDenial'>
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.none_of_above',
                            isLabelRequired: false
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'hasSomeoneWithMedicaidChipDenial',
                            checked: hasSomeoneWithMedicaidChipDenial === false,
                            value: hasSomeoneWithMedicaidChipDenial === false,
                            currentData: cloneObject(data),
                            fields: [ 'taxHousehold', 'thCommonParams' ],
                            reversedValue: true
                        } }
                        inputActions={ {
                            onFieldChange: onFieldChange_none_of_above
                        } }
                    />
                </li>
            </ul>
        );
    };

    const getHouseholdMembersBHP = (householdMembers) => {
        const membersVisible = householdMembers
            .map((member, i) => {
                const isVisible = questionsVisibility.isMedicaidChipDenialShown({
                    hhm: member,
                    medicaidTerminationFeatureEnabled,
                    serverDate,
                    medicaidEndDateOverride
                });
                return { member, originalIndex: i, isVisible };
            })
            .filter(({ isVisible }) => isVisible);

        const combinedMembers = membersVisible.map(({ member, originalIndex: i }) => {
            const {
                applicantGuid,
                name,
                bhpDenied = null,
                bhpDenialDate = null
            } = member;

            const memberName = normalizeNameOfPerson(name);

            return (
                <li key = { `bhp-${applicantGuid}` }>
                    <UniversalInput
                        label={{
                            text: 'common.input.label.non_internationalized_text',
                            labelTextReplacements: {
                                text: memberName
                            }
                        }}
                        inputData={{
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'bhpDenied',
                            classes: [''],
                            checked: bhpDenied,
                            value: bhpDenied,
                            currentData: member,
                            fields: [],
                        }}
                        inputActions={{
                            onFieldChange: onFieldChange_member
                        }}
                        indexInArray={i}
                    />

                    <ConditionalRendering isTrue={bhpDenied} >
                        <div className='grid-container'>
                            <UniversalInput
                                id='medicaid_chip_denial_information'
                                label={{
                                    labelClass: ['padding-0'],
                                    text: 'ssap.input.label.when_denied_bhp',
                                    labelTextReplacements: {
                                        personName: memberName
                                    },
                                    dataInstructions: 'instructional_text.bhp_denial_information',
                                    replacements: { exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip }
                                }}
                                inputData={{
                                    inputClass: [''],
                                    isInputRequired: true,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    name: 'bhpDenialDate',
                                    value: bhpDenialDate,
                                    valuesToMatch: serverDate,
                                    validationType: VALIDATION_TYPES.DATE_VALID_CHIP_DENIAL_DATE,
                                    currentData: member,
                                    fields: []
                                }}
                                inputActions={{
                                    onFieldChange: onFieldChange_member
                                }}
                                errorClasses={['margin-l-0']}
                                indexInArray={i}
                            />
                        </div>
                    </ConditionalRendering>
                </li>
            );
        });

        return (
            <ul className="usa-list usa-list--unstyled" role="group" aria-labelledby="bhp_denial_information_legend_text">
                {combinedMembers}

                <li key='hasSomeoneWithBHPDenial'>
                    <UniversalInput
                        label={{
                            text: 'ssap.input.label.none_of_above',
                            isLabelRequired: false
                        }}
                        inputData={{
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'hasSomeoneWithBHPDenial',
                            checked: hasSomeoneWithBHPDenial === false,
                            value: hasSomeoneWithBHPDenial === false,
                            currentData: cloneObject(data),
                            fields: ['taxHousehold', 'thCommonParams'],
                            reversedValue: true
                        }}
                        inputActions={{
                            onFieldChange: onFieldChange_none_of_above
                        }}
                    />
                </li>
            </ul>
        );
    };

    const topLevelValidityMedicaid = useMemo(
        () => {
            return householdMembers.some(hhm => hhm.medicaidChipDenial) || hasSomeoneWithMedicaidChipDenial === false;
        },
        [householdMembers, hasSomeoneWithMedicaidChipDenial]
    );

    const topLevelValidityBHP = useMemo(
        () => {
            return !isState.MN || householdMembers.some(hhm => hhm.bhpDenied) || hasSomeoneWithBHPDenial === false;
        },
        [householdMembers, hasSomeoneWithBHPDenial]
    );

    return (
        <Fragment>
            <Form
                { ...{
                    formName,
                    topLevelValidity: topLevelValidityMedicaid && topLevelValidityBHP
                } }
            >
                <div
                    className = 'fade-in'
                    id = 'fhMoreInfo'
                >
                    <div className = 'subsection' >
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !topLevelValidityMedicaid }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                        <fieldset>
                            <legend data-testid='medicaid_chip_denial_information_legend'>
                                <InstructionalText
                                    Component = {
                                        <p data-instructions='instructional_text.medicaid_chip_denial_information.general_notice'  id="medicaid_chip_denial_information_legend_text">
                                            { 'ssap.family_household.medicaid.ninety_days' }
                                        </p>
                                    }
                                    replacements={{ exchangeName, exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip, br: <br /> }}
                                    childrenReplacements = { { exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip } }
                                    
                                    required
                                />
                            </legend>
                            { getHouseholdMembers(householdMembers) }
                        </fieldset>

                        <ConditionalRendering isTrue={isState.MN}>
                            <ValidationErrorMessage
                                isVisible={isFormSubmitted && !topLevelValidityBHP}
                                messageText={VALIDATION_ERROR_MESSAGES.ANY_VALUE}
                                errorClasses={['margin-l-0']}
                            />
                            <fieldset>
                                <legend data-testid='bhp_denial_information_legend'>
                                    <InstructionalText
                                        Component={
                                            <p data-instructions='instructional_text.bhp_denial_information.general_notice' id="bhp_denial_information_legend_text">
                                                {'ssap.family_household.bhp.ninety_days'}
                                            </p>
                                        }
                                        replacements={{ exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip }}
                                        childrenReplacements={{ exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip }}

                                        required
                                    />
                                </legend>
                                {getHouseholdMembersBHP(householdMembers)}
                            </fieldset>
                        </ConditionalRendering>
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_21);
